import { BridgeComponent } from "@hotwired/hotwire-native-bridge"
import { createConsumer } from "@rails/actioncable"
import { canvasResize, PHOTO_THUMBNAIL, BLUR_PLACEHOLDER } from "../lib/image_resize"
import { csrfToken, postJson } from "../lib/http"
import { uploadThumbnailsInChunks } from "../lib/thumbnail_backfill"

// Bridge controller on the discoveries feed + match page (native only).
//
// 1. Display: replaces [data-client-ref] placeholders with the actual photo from
//    THIS device's library (the only place that photo can come from live).
// 2. Backfill: uploads thumbnails for this user's MATCHED photos to the DB so the
//    OTHER person can see them — proactively when matching completes (MatchingChannel
//    push) and as a catch-up on connect. Matched photos only, never the whole library.
//
// Does nothing on web (BridgeComponent.shouldLoad returns false without a bridge).
export default class extends BridgeComponent {
  static component = "photo-library"

  connect() {
    super.connect()
    setTimeout(() => this._loadAll(), 150)

    // Catch-up now (in case matching already finished), then stay subscribed so a
    // match created while we're here triggers the backfill immediately.
    this._backfillMatched()
    this._consumer = createConsumer()
    this._subscription = this._consumer.subscriptions.create(
      { channel: "MatchingChannel" },
      { received: (data) => { if (data?.type === "matching_complete") this._backfillMatched() } }
    )
  }

  disconnect() {
    this._subscription?.unsubscribe()
    this._consumer?.disconnect()
    super.disconnect()
  }

  // ── Display: swap placeholders with device photos ─────────────────────────

  _loadAll() {
    const groups = {}
    this.element.querySelectorAll("[data-client-ref]").forEach(el => {
      const ref = el.dataset.clientRef
      if (!ref) return
      if (!groups[ref]) groups[ref] = []
      groups[ref].push(el)
    })

    Object.entries(groups).forEach(([localIdentifier, elements]) => {
      this._fetchAndApply(localIdentifier, elements)
    })
  }

  async _fetchAndApply(localIdentifier, elements) {
    // Skip web-uploaded photos — they're not in the device photo library.
    if (localIdentifier.startsWith("web-")) return

    const src = await this._fetchPhoto(localIdentifier)
    if (!src) return

    elements.forEach(el => {
      if (!el.isConnected) return
      const img = document.createElement("img")
      img.src             = src
      img.style.width     = "100%"
      img.style.height    = "100%"
      img.style.objectFit = "cover"
      img.style.display   = "block"
      el.replaceWith(img)
    })

    this._uploadThumbnail(localIdentifier, src)
  }

  // Fetch one photo from this device, as a data URL (or null). Serialized and
  // deduped: the Hotwire bridge keys replies by event name, so two concurrent
  // "fetchPhoto" sends clobber each other's callback (the display fetch and the
  // thumbnail-backfill fetch were racing — one callback never fired, leaving a
  // permanent camera icon). One send at a time, one fetch per identifier.
  _fetchPhoto(localIdentifier) {
    this._cache ||= {}
    if (localIdentifier in this._cache) return this._cache[localIdentifier]

    const result = (this._queue || Promise.resolve()).then(() => new Promise((resolve) => {
      let done = false
      const finish = (value) => { if (!done) { done = true; resolve(value) } }
      // Don't let a dropped reply stall the whole queue.
      setTimeout(() => finish(null), 8000)
      this.send("fetchPhoto", { localIdentifier }, (response) => {
        const base64   = response?.data?.base64   || response?.base64
        const mimeType = response?.data?.mimeType || response?.mimeType || "image/jpeg"
        finish(base64 ? `data:${mimeType};base64,${base64.replace(/\s/g, "")}` : null)
      })
    }))

    this._queue = result.catch(() => {})
    this._cache[localIdentifier] = result
    return result
  }

  // ── Backfill: upload this user's matched-photo thumbnails to the DB ────────

  async _backfillMatched() {
    try {
      const res = await fetch("/library_photos/match_missing_thumbnails", {
        headers: { "X-CSRF-Token": csrfToken() }
      })
      if (!res.ok) return
      const { client_refs } = await res.json()
      await uploadThumbnailsInChunks(client_refs, (ref) => this._fetchThumbnail(ref))
    } catch (e) {
      // Non-fatal — thumbnails are cosmetic.
    }
  }

  // Fetch a photo from this device and return a resized thumbnail data URL, or null.
  async _fetchThumbnail(localIdentifier) {
    const src = await this._fetchPhoto(localIdentifier)
    if (!src) return null
    return canvasResize(src, PHOTO_THUMBNAIL)
  }

  async _uploadThumbnail(clientRef, dataUrl) {
    const thumbnail = await canvasResize(dataUrl, PHOTO_THUMBNAIL)
    if (!thumbnail) return
    // Send the blur too, so a photo whose image lands via this display path still
    // gets its reveal placeholder — otherwise it shows the neutral "Not revealed
    // yet" lock to the other person instead of a blurred preview.
    const blur = await canvasResize(dataUrl, BLUR_PLACEHOLDER)
    postJson("/library_photos/thumbnails", {
      thumbnails: [{ client_ref: clientRef, data: thumbnail, blur }]
    }).catch(() => {})
  }
}
